import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import { QueueList } from '../components/QueueList'
import { accountsApi, type QueueItem } from '../../accounts/lib/accounts'

/**
 * /queue — Publishing Queue: semua yang menunggu terbit di semua akun.
 *
 * Antreannya tetap milik Worker platform (urutan, slot, dispatch ke agent);
 * halaman ini hanya menggabungkannya urut waktu tayang dan meneruskan aksi.
 */
export default function PublishingQueuePage() {
  const [items, setItems] = useState<QueueItem[] | null>(null)
  const [unavailable, setUnavailable] = useState<string[]>([])
  const [filter, setFilter] = useState('')

  const muat = useCallback(() => {
    accountsApi
      .queue()
      .then((r) => {
        setItems(r.items)
        setUnavailable(r.unavailable)
      })
      .catch((err) => {
        setItems([])
        toast.error(err instanceof Error ? err.message : "Couldn't load the queue")
      })
  }, [])

  useEffect(() => muat(), [muat])

  const akun = useMemo(() => {
    const m = new Map<string, string>()
    for (const i of items ?? []) m.set(i.workspaceId, i.workspaceName)
    return [...m]
  }, [items])
  const tampil = filter ? (items ?? []).filter((i) => i.workspaceId === filter) : (items ?? [])

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Publishing Queue</h1>
          <p className="text-sm text-muted-foreground">
            Everything waiting to be published, across all your accounts. Times are in your time zone.
          </p>
        </div>
        {akun.length > 1 && (
          <select
            aria-label="Filter by account"
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="">All accounts</option>
            {akun.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
        )}
      </header>

      {unavailable.length > 0 && (
        <p className="text-sm text-amber-400">
          Couldn't load the queue for {unavailable.join(', ')}. Try again in a moment.
        </p>
      )}

      {items === null ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading…
        </div>
      ) : tampil.length === 0 ? (
        <div className="space-y-1 text-sm text-muted-foreground">
          <p>Nothing is waiting to be published.</p>
          <p>
            Posts you schedule land here. Set when each account posts under{' '}
            <Link to="/projects" className="text-foreground underline-offset-4 hover:underline">Projects</Link> → Publishing.
          </p>
        </div>
      ) : (
        <QueueList items={tampil} onChanged={muat} />
      )}
    </div>
  )
}
